import axios from 'axios';
import { getCacheStandalone, setCacheStandalone, invalidateCacheStandalone } from './cache';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// ============================================
// AXIOS INSTANCE
// ============================================
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// CACHED GET REQUEST
// ============================================
const cachedGet = async (url, useCache = true) => {
  if (useCache) {
    const cachedData = getCacheStandalone(url);
    if (cachedData) {
      console.log(`[Cache HIT] ${url}`);
      return cachedData;
    }
  }
  
  console.log(`[Cache MISS] ${url}`);
  const response = await api.get(url);
  setCacheStandalone(url, response);
  return response;
};

// Invalidar caché
export const invalidateCache = (section) => {
  const patterns = {
    clients: '/clients',
    executives: '/executives',
    forecast: '/forecast',
    summary: '/summary',
    coverage: '/coverage',
    pipeline: '/pipeline',
    all: null
  };
  
  const pattern = patterns[section] || section;
  invalidateCacheStandalone(pattern);
  
  // Si se invalida pipeline, también invalidar forecast y summary
  if (section === 'pipeline') {
    invalidateCacheStandalone('/forecast');
    invalidateCacheStandalone('/summary');
  }
};

// ============================================
// AUTH (no cache)
// ============================================
export const login = (email, password) => 
  api.post('/auth/login', { email, password });

export const register = (email, name, password) => 
  api.post('/auth/register', { email, name, password });

export const getMe = () => api.get('/auth/me');

// ============================================
// EXECUTIVES (cached)
// ============================================
export const getExecutives = () => cachedGet('/executives');
export const getExecutive = (id) => cachedGet(`/executives/${id}`);
export const getExecutiveClients = (id) => cachedGet(`/executives/${id}/clients`);
export const getExecutivePipeline = (id) => cachedGet(`/executives/${id}/pipeline`);

// ============================================
// CLIENTS (cached)
// ============================================
export const getClients = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return cachedGet(`/clients${queryString ? `?${queryString}` : ''}`);
};
export const getClient = (id) => cachedGet(`/clients/${id}`);
export const getClientForecast = (id) => cachedGet(`/clients/${id}/forecast`);
export const getClientPipeline = (id) => cachedGet(`/clients/${id}/pipeline`);
export const createClient = (data) => {
  invalidateCache('clients');
  return api.post('/clients', data);
};

// ============================================
// PIPELINE (invalidates cache on write)
// ============================================
export const getPipeline = () => cachedGet('/pipeline');
export const createPipeline = (data) => {
  invalidateCache('pipeline');
  return api.post('/pipeline', data);
};
export const updatePipeline = (id, data) => {
  invalidateCache('pipeline');
  return api.patch(`/pipeline/${id}`, data);
};

// ============================================
// FORECAST (cached - heavy ML operations)
// ============================================
export const getGlobalForecast = () => cachedGet('/forecast/global');
export const getScenarioForecast = (data) => api.post('/forecast/scenario', data);

// ============================================
// WEEKLY SUMMARY (cached)
// ============================================
export const getWeeklySummary = () => cachedGet('/summary/weekly');

// ============================================
// MARKET COVERAGE (cached)
// ============================================
export const getMarketCoverage = () => cachedGet('/coverage/market');

// ============================================
// DATA ENTRY (invalidates all cache)
// ============================================
export const addVolumeEntry = (data) => {
  invalidateCache('all');
  return api.post('/data/volume', data);
};
export const updatePenta = (data) => {
  invalidateCache('all');
  return api.post('/data/penta', data);
};
export const getRecentEntries = () => api.get('/data/entries');

// ============================================
// MODEL INFO (cached)
// ============================================
export const getModelInfo = () => cachedGet('/model/info');
export const getModelAccuracy = () => cachedGet('/model/accuracy');
export const downloadModelPdf = () => api.get('/model/pdf', { responseType: 'blob' });
export const downloadTechnicalDoc = () => api.get('/model/technical-doc', { responseType: 'blob' });

// ============================================
// ROUTES (cached)
// ============================================
export const getRoutes = () => cachedGet('/routes');

export default api;
